import React from "react";

import { app } from "js/namespaces";
import log from "js/core/logger";
import { Slide } from "js/core/models/slide";
import Api from "js/core/api";
import { ds } from "js/core/models/dataService";

import { LoadingPage } from "js/react/components/LoadingPage";
import { withFirebaseUser } from "js/react/views/Auth/FirebaseUserContext";
import { prepareToUnlockAudio } from "js/core/utilities/audioUtilities";

import PlayerContext from "./PlayerContext";
import { getAnalytics, AnalyticsContextType } from "../helpers/analytics";
import { FeatureType } from "common/features";

class PlayerEditorContext extends PlayerContext {
    constructor() {
        super();

        this.state = {
            fetching: true
        };

        this.originalSelectionPresentation = null;
        this.shouldRestoreOriginalSelectionPresentation = false;
    }

    componentDidMount() {
        this.load();
    }

    componentWillUnmount() {
        this.dispose();
    }

    async load() {
        const { presentation, isOpenedFromMainView, firebaseUser } = this.props;

        try {
            // Will make sure that the selection is in the right state
            if (ds.selection.presentation !== presentation) {
                this.shouldRestoreOriginalSelectionPresentation = true;
                this.originalSelectionPresentation = ds.selection.presentation;
                ds.selection.presentation = presentation;
            }

            const slidesMetadata = await Api.presentationSlidesMetadata.get({ id: presentation.id });
            ds.selection.presentation.slidesMetadata = slidesMetadata;

            // Note: slides won't be preloaded, the player will take care of loading them when needed
            const slides = presentation.getSips()
                .map(slideId => new Slide({ id: slideId }, { presentation, autoLoad: false }))
                .reduce((slides, slide) => ({ ...slides, [slide.id]: slide }), {});

            const analytics = getAnalytics(isOpenedFromMainView ? AnalyticsContextType.EDITOR_MAINVIEW : AnalyticsContextType.EDITOR, false, firebaseUser, presentation.permissions.collaborator, presentation);

            this.contextData = {
                ...this.contextData,
                isLoggedIn: true,
                presentation,
                isPreviewFromEditor: true,
                showBranding: !app.user.features.isFeatureEnabled(FeatureType.PLAYER_SETTINGS, presentation.getWorkspaceId()),
                isCreatorOldBasicUser: app.user.isOldBasicUser(),
                slides,
                slidesMetadata,
                requireEmail: false,
                username: firebaseUser.email,
                onCopyDeck: false,
                allowCopyLink: false,
                allowSocialSharing: false,
                onSwitchPresentation: false,
                onEditPresentation: false,
                onCopyPresentation: false,
                onDownloadPdf: false,
                creator: {
                    displayName: firebaseUser.displayName || firebaseUser.email,
                    photoURL: firebaseUser.photoURL
                },
                isPresenterAvailable: !app.isMobileOrTablet,
                presenterUrl: "/presenter",
                presenterWindowTarget: "_blank",
                analytics,
            };

            // Load theme
            await app.themeManager.loadTheme(presentation);

            // Preload data necessary for synchronous audio playback unlocking
            await prepareToUnlockAudio();

            this.setState({ fetching: false });
        } catch (err) {
            log.error(err);
        }
    }

    dispose() {
        if (this.shouldRestoreOriginalSelectionPresentation) {
            ds.selection.presentation = this.originalSelectionPresentation;
        }

        if (!this.contextData) {
            return;
        }

        // Disconnecting all slides
        Object.values(this.contextData.slides).forEach(slide => slide.disconnect());

        // NOTE: we won't disconnect the presentation because it's been forwarded by the editor
    }

    render() {
        const { fetching } = this.state;

        if (fetching) {
            return <LoadingPage />;
        }

        return this.renderChildrenWithContext();
    }
}

export default withFirebaseUser(PlayerEditorContext);
